import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  model,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent, FilesToUpload } from '@akostm/orbit-ui';
import {
  faCamera,
  faImage,
  faLocationDot,
  faMicrophone,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { FooterButtonType, MessageSenderType } from '../../../interfaces';
import { MessageKind, MessageTemplateLight, ConversationTypeTemplateKind } from '@clientwire/api';
import { faLocationQuestion } from '@fortawesome/pro-solid-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'shared-footer-addon',
  standalone: true,
  imports: [CommonModule, ButtonComponent, TranslatePipe],
  templateUrl: './footer-addon.component.html',
  styleUrl: './footer-addon.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterAddonComponent {
  senderType = input.required<MessageSenderType>();
  templates = input<Map<string, MessageTemplateLight> | undefined>();
  deletedAttachment = input<MessageKind | null>(null);
  files = model<FilesToUpload[]>();
  buttonClick = output<FooterButtonType>();
  photoUploader = viewChild<ElementRef>('photoUploader');
  cameraUploader = viewChild<ElementRef>('cameraUploader');
  audioUploader = viewChild<ElementRef>('audioUploader');
  videoUploader = viewChild<ElementRef>('audioUploader');

  readonly imageIcon = faImage;
  readonly cameraIcon = faCamera;
  readonly microphoneIcon = faMicrophone;
  readonly locationDotIcon = faLocationDot;
  readonly locationRequestIcon = faLocationQuestion;
  readonly videoIcon = faVideo;
  readonly footerButtonTypeEnum = FooterButtonType;
  readonly messageSenderTypeEnum = MessageSenderType;

  readonly showRequestLocationButton = computed(() => {
    const templates = this.templates();
    return templates?.has(ConversationTypeTemplateKind.RequestLocation) ?? false;
  });

  readonly isSafari = signal(false);
  readonly supportsCaptureAttribute = signal(false);

  constructor() {
    this.isSafari.set(
      !!(
        navigator.userAgent.match(/safari/i) &&
        navigator.vendor.match(/apple/i) &&
        navigator.maxTouchPoints
      )
    );

    // Feature detection for capture attribute
    this.supportsCaptureAttribute.set(this.#detectCaptureSupport());

    effect(() => {
      const photoUploader = this.photoUploader();
      const cameraUploader = this.cameraUploader();
      const audioUploader = this.audioUploader();
      const videoUploader = this.videoUploader();
      const dA = this.deletedAttachment();
      if (this.deletedAttachment()) {
        switch (dA) {
          case 'IMAGE':
            if (photoUploader?.nativeElement?.value) {
              photoUploader.nativeElement.value = '';
            }
            if (cameraUploader?.nativeElement?.value) {
              cameraUploader.nativeElement.value = '';
            }
            break;
          case 'VIDEO':
            if (videoUploader?.nativeElement?.value) {
              videoUploader.nativeElement.value = '';
            }
            break;
          case 'AUDIO':
            if (audioUploader?.nativeElement?.value) {
              audioUploader.nativeElement.value = '';
            }
            break;
        }
      }
    });
  }

  onFileSelected(event: any, type: FooterButtonType) {
    if (event && event.target) {
      const array: FilesToUpload[] = event.target.files
        ? Array.from(event.target.files).map((file) => {
            return {
              file: file as File,
              src: '',
            };
          })
        : [];
      this.files.set(array);
    }
  }

  onCameraButtonClick() {
    if (this.supportsCaptureAttribute()) {
      // If capture is supported, trigger the file input
      this.cameraUploader()?.nativeElement?.click();
    } else {
      // If capture is not supported, emit the WebCamera button event
      this.buttonClick.emit(this.footerButtonTypeEnum.WebCamera);
    }
  }

  #detectCaptureSupport(): boolean {
    // Create a temporary input element to test capture support
    const input = document.createElement('input');
    input.type = 'file';

    // Check if the capture property exists and if it's supported
    return 'capture' in input && this.#isMobileDevice();
  }

  #isMobileDevice(): boolean {
    // Check if it's a mobile device where capture attribute is most useful
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      navigator.maxTouchPoints > 0
    );
  }
}
